import { useState, useRef, useEffect } from "react";
import EmojiPicker, { EmojiClickData, Theme } from "emoji-picker-react";
import styled from "styled-components";
import { BsFillEmojiLaughingFill, BsEmojiHeartEyesFill } from "react-icons/bs";
import { IconType } from "react-icons";
import { Box } from "rebass";

type EmojiPosition = "top" | "bottom" | "left" | "right";

interface EmojiSelectorProps {
  onEmojiSelect?: (emoji: string) => void;
  icon?: IconType;
  position?: EmojiPosition;
}

export const EmojiSelector = ({
  onEmojiSelect,
  position = "top",
}: EmojiSelectorProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const handleEmojiClick = (emojiData: EmojiClickData) => {
    onEmojiSelect?.(emojiData.emoji);
    setIsOpen(false);
  };

  const togglePicker = () => {
    setIsOpen(!isOpen);
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  return (
    <EmojiSelectorWrapper ref={containerRef}>
      <EmojiButton
        ref={buttonRef}
        onClick={togglePicker}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        {isHovered ? (
          <BsEmojiHeartEyesFill size={18} />
        ) : (
          <BsFillEmojiLaughingFill size={18} />
        )}
      </EmojiButton>

      {isOpen && (
        <EmojiPickerContainer position={position}>
          <CustomEmojiPickerWrapper>
            <EmojiPicker theme={Theme.DARK} onEmojiClick={handleEmojiClick} />
          </CustomEmojiPickerWrapper>
        </EmojiPickerContainer>
      )}
    </EmojiSelectorWrapper>
  );
};

const EmojiSelectorWrapper = styled.div`
  position: relative;
  display: inline-block;
  overflow: visible;
`;

const EmojiButton = styled.button`
  align-items: center;
  background: transparent;
  border: none;
  border-radius: 99px;
  color: ${({ theme }) => theme.colors.shade300};
  cursor: pointer;
  display: flex;
  font-size: 16px;
  font-size: 16px;
  height: 22px;
  justify-content: center;
  padding: 0;
  padding: 0;
  transition: all 0.2s ease-in-out;
  width: 22px;
  width: 22px;

  &:hover {
    color: ${({ theme }) => theme.colors.envy}
`;

const EmojiPickerContainer = styled(Box)<{ position: EmojiPosition }>`
  position: absolute;
  z-index: 9999;
  box-shadow: 0px 4px 24px rgba(0, 0, 0, 0.25);
  animation: fadeIn 0.2s ease-in-out;

  @keyframes fadeIn {
    from {
      opacity: 0;
      transform: scale(0.95) translateY(-5px);
    }
    to {
      opacity: 1;
      transform: scale(1) translateY(0);
    }
  }

  ${({ position }) => {
    switch (position) {
      case "top":
        return `
          bottom: 100%;
          right: 0;
          margin-bottom: 8px;
        `;
      case "bottom":
        return `
          top: 100%;
          right: 0;
          margin-top: 8px;
        `;
      case "left":
        return `
          top: 0;
          right: 100%;
          margin-right: 8px;
        `;
      case "right":
        return `
          top: 0;
          left: 100%;
          margin-left: 8px;
        `;
      default:
        return `
          top: 100%;
          right: 0;
          margin-top: 8px;
        `;
    }
  }}

  @media (max-width: 768px) {
    transform: translateX(40px);
  }
`;

const CustomEmojiPickerWrapper = styled(Box)`
  .epr-main {
    background-color: ${({ theme }) => theme.colors.ned};
    border: 1px solid ${({ theme }) => theme.colors.navy50};
    border-radius: 8px;
  }

  .epr-search-container {
    --epr-search-input-bg-color: ${({ theme }) => theme.colors.navy500};

    input {
      &:focus {
        border-color: ${({ theme }) => theme.colors.navy50} !important;
        background-color: ${({ theme }) => theme.colors.darkerNed} !important;
      }
    }
  }

  .epr-emoji-category-label {
    background-color: ${({ theme }) => theme.colors.navy500};
  }

  .epr-header {
    background-color: ${({ theme }) => theme.colors.ned};
  }

  .epr-button {
    --epr-dark-category-icon-active-color: ${({ theme }) => theme.colors.envy};
  }

  .epr-category-nav {
    background-color: ${({ theme }) => theme.colors.ned};
  }

  .epr-body {
    background-color: ${({ theme }) => theme.colors.ned};
    --epr-dark-category-icon-active-color: ${({ theme }) => theme.colors.envy};
  }
`;
